import { useCallback, useEffect } from "react"

import { api } from "@/lib/api"
import { useSession } from "@/lib/session"
import { useSetupWorkspace } from "@/lib/states/setup-workspace-state"

// Step 1 creates or joins the workspace; every step after it writes to that workspace.
const FIRST_STEP_IN_WORKSPACE = 2

/**
 * The workspace to switch back to when the session moved off the one being set up.
 * The session is one cookie for every tab, so switching workspace anywhere moves it.
 */
export function driftedSetupWorkspace({
	step,
	setupWorkspaceId,
	sessionWorkspaceId,
}: {
	step: number
	setupWorkspaceId: string | null
	sessionWorkspaceId: string | null
}): string | null {
	if (step < FIRST_STEP_IN_WORKSPACE) return null
	if (!setupWorkspaceId || !sessionWorkspaceId) return null
	return sessionWorkspaceId === setupWorkspaceId ? null : setupWorkspaceId
}

export function useSetupWorkspacePin(step: number) {
	const { session } = useSession()
	const { workspaceId, setWorkspaceId } = useSetupWorkspace()
	const utils = api.useUtils()
	const changeWorkspace = api.auth.changeWorkspace.useMutation()

	const target = driftedSetupWorkspace({
		step,
		setupWorkspaceId: workspaceId,
		sessionWorkspaceId: session?.workspaceId ?? null,
	})

	useEffect(() => {
		if (!target) return
		changeWorkspace.mutate(
			{ workspaceId: target },
			{
				onSuccess: () => void utils.invalidate(),
				// Gone or no longer a member: finish on the workspace the session is on.
				onError: () => setWorkspaceId(null),
			},
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [target])

	/** Remembers the workspace the session is on now, right after step 1 created or joined it. */
	const pin = useCallback(async () => {
		try {
			const fresh = await utils.user.info.fetch(undefined, { staleTime: 0 })
			setWorkspaceId(fresh?.workspaceId ?? null)
		} catch {
			setWorkspaceId(null)
		}
	}, [setWorkspaceId, utils])

	return { pin, isSwitchingBack: target !== null }
}
